import React, { useEffect, useState } from "react";
import { Ticket } from "@/app/types/tickets";
import { TicketStatus, useGetAllUserListingOfTicketsQuery } from "@/services/LiveQueueService";
import useTableData from "@/components/ui/table/DataTableLogic";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store";
import { useNavigate, useParams } from "react-router-dom";
import { debounce } from "lodash";
import { messaging } from "@/utils/lib/Firebase";
import { Messaging, onMessage } from "firebase/messaging";
import { TicketItem } from "@/components/remote-user/components/TicketItem";
import { TicketList } from "@/components/remote-user/components/TicketList";
import { useToast } from "@/components/ui/UseToast";

const { VITE_IS_PRODUCTION, VITE_IS_LOCAL, VITE_LOCAL_URL } = import.meta.env;
const statusOptions = [
  { value: "PENDING", label: "Pending" }, // Custom pending option
  { value: TicketStatus.CHECKED_IN, label: "Checked In" },
  { value: TicketStatus.NO_SHOW, label: "No Show" },
  { value: TicketStatus.IN_PROGRESS, label: "In Progress" },
  { value: TicketStatus.AWAITING_INFO, label: "Awaiting Info" },
  { value: TicketStatus.HOLD, label: "Hold" },
  { value: TicketStatus.ARCHIVED, label: "Archived" },
  { value: TicketStatus.RESERVED, label: "Reserved" },
];

export default function CheckMyTicketTicketList() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const {toast} = useToast();

  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  const [selectedStatus, setSelectedStatus] = useState<TicketStatus | null>(
    null
  ); // Default to null (Show All)
  const { itsId } = useParams<{ itsId: string }>();

  const initialValue = {
    limit: 10,
    offset: 0,
    sort_order: "desc",
    sort_key: "id",
    its_number: itsId,//userInfo?.id,
    domain: VITE_IS_LOCAL === "true"
      ? VITE_LOCAL_URL
      : VITE_IS_PRODUCTION === "true"
        ? window.location.hostname
        : window.location.host,
    search: searchQuery || "S-", // Default to "s" if searchQuery is empty
    status: statusOptions.reduce((statuses, option) => option.value === "PENDING" ? [...statuses, TicketStatus.NEW, TicketStatus.ARRIVED] : [...statuses, option.value] , [] as string[])
    
  };
  const { searchCriteria, query, toggleSortOrder, setSearchCriteria } =
    useTableData(initialValue);

  const {
    data: departmentData,
    isLoading: isDepartmentDataLoading,
    refetch,
    error,
    isError,
  } = useGetAllUserListingOfTicketsQuery(
    {
      query: query,
    },
    {
      skip: query == "",
    }
  );
  
  const totalRecords = departmentData?.filtered_count ?? 0;

  const handleSearch = React.useCallback(
    debounce((term: string) => {
      setSearchCriteria((prev) => ({ ...prev, search: term })); // pass the correct term here
    }, 500),
    []
  );

  React.useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    messaging().then((msgInstance: Messaging | null) => {
      if (!msgInstance) {
        console.error("Firebase Messaging is not initialized.");
        return;
      }

      unsubscribe = onMessage(msgInstance, (payload) => {
        if (payload.notification) {
          toast({
            variant: "default",
            description: payload.notification.body,
            title: payload.notification.title,
          });
        }
        const { data } = payload;
        if (data?.type && data?.id) {}
      });
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  React.useEffect(() => {
    handleSearch(searchQuery); 
  }, [searchQuery]);

  useEffect(() => {
    setSearchCriteria((prev) => {
      const updatedCriteria: any = { ...prev };

      if (searchQuery) {
        updatedCriteria.search = searchQuery;
      }

      if (selectedStatus) {
        if (selectedStatus === "PENDING" as unknown as TicketStatus) {
          updatedCriteria.status = [TicketStatus.NEW, TicketStatus.ARRIVED]; // Include both statuses
        } else if (Object.values(TicketStatus).includes(selectedStatus)) {
          updatedCriteria.status = selectedStatus;
        }
      } else {
          updatedCriteria.status = statusOptions.reduce((statuses, option) => option.value === "PENDING" ? [...statuses, TicketStatus.NEW, TicketStatus.ARRIVED] : [...statuses, option.value] , [] as string[])
      }

      return updatedCriteria;
    });
  }, [searchQuery, selectedStatus, setSearchCriteria]);

  const renderItem = (ticket: Ticket) => (
    <TicketItem ticket={ticket} />
  );

  return (
    <TicketList<typeof searchCriteria>
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      selectedStatus={selectedStatus}
      setSelectedStatus={setSelectedStatus}
      statusOptions={statusOptions}
      departmentData={departmentData}
      isError={isError}
      isDepartmentDataLoading={isDepartmentDataLoading}
      searchCriteria={searchCriteria}
      setSearchCriteria={setSearchCriteria}
      totalRecords={totalRecords}
      renderItem={renderItem}
    />
  );
}
